import { FailureApi } from "@smp/api";
import {
  dataIsObject,
  dataObjectKeys,
  dataObjectsDeepEqual,
  Failure,
  testAppendAttr,
} from "@smp/utils";
import { FormikErrors, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePrevious, useUpdateEffect } from "react-use";

import { CardFailure } from "@/components/ui/card_failure";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { isGqlError } from "@/utils";

import { IFormErrorsProps } from "./form_errors.types";

export const FormErrors = <IFormValues extends Record<string, any>>({
  clearStatusOnFieldChange = false,
}: IFormErrorsProps) => {
  const {
    errors,
    setErrors,
    setStatus,
    status: failureOrError,
    values,
    isSubmitting,
  } = useFormikContext<IFormValues>();
  const { failureTranslationGet } = useFailureTranslator();

  const [failureOrErrorCommon, setFailureOrErrorCommon] = useState<
    Error | Failure | FailureApi | undefined
  >(undefined);
  const [formFieldsApiError, setFormFieldsApiError] = useState<
    FormikErrors<IFormValues> | undefined
  >(undefined);
  const valuesPrevious = usePrevious(values);
  const failureOrErrorPrevious = usePrevious(failureOrError);

  const formFieldsApiErrorKeys = useMemo(
    () => (formFieldsApiError ? dataObjectKeys(formFieldsApiError) : []),
    [formFieldsApiError],
  );

  const valuesFields = useMemo(() => dataObjectKeys(values), [values]);

  const fieldStatusClear = useCallback(
    (fieldName: keyof IFormValues, status: any) => {
      const failuresApi = status?.failures;

      if (!failuresApi) {
        return;
      }

      if (failuresApi?.[fieldName]) {
        delete failuresApi[fieldName];
        if (!dataObjectKeys(failuresApi).length) {
          setStatus();
          return true;
        }
      }
      return false;
    },
    [setStatus],
  );

  useUpdateEffect(() => {
    if (formFieldsApiError && formFieldsApiErrorKeys.length) {
      const errorKeysClearedByFormik = formFieldsApiErrorKeys.filter(
        (key) => !dataObjectKeys(errors).includes(key),
      );
      if (errorKeysClearedByFormik.length) {
        const clearedErrors = errorKeysClearedByFormik.reduce(
          (acc, errorKey) => ({
            ...acc,
            [errorKey]: formFieldsApiError[errorKey],
          }),
          {} as FormikErrors<IFormValues>,
        );
        setErrors({ ...errors, ...clearedErrors });
      }
    }
  }, [
    formFieldsApiError,
    formFieldsApiErrorKeys,
    formFieldsApiErrorKeys.length,
    setErrors,
  ]);

  useEffect(() => {
    if (clearStatusOnFieldChange && formFieldsApiError && valuesPrevious) {
      if (!dataObjectsDeepEqual(values, valuesPrevious)) {
        const valuesKey = dataObjectKeys(values);
        const valuesChangedKeys = valuesKey.filter((key) => {
          const currentValue = values[key];
          const oldValue = valuesPrevious[key];
          const isObjects =
            dataIsObject(currentValue) && dataIsObject(oldValue);
          if (
            (isObjects && !dataObjectsDeepEqual(currentValue, oldValue)) ||
            (!isObjects && currentValue !== oldValue)
          ) {
            return true;
          }
          return false;
        });
        const errorKeys = dataObjectKeys(formFieldsApiError);

        if (valuesChangedKeys.length) {
          const clearFieldErrorStatuses = valuesChangedKeys
            .filter((changedKey) => errorKeys.includes(changedKey))
            .map((changedKey) => fieldStatusClear(changedKey, failureOrError));
          const isAllApiErrorsCleared =
            clearFieldErrorStatuses[clearFieldErrorStatuses.length - 1];

          if (isAllApiErrorsCleared) {
            setFormFieldsApiError(undefined);
          }
        }
      }
    }
  }, [
    clearStatusOnFieldChange,
    failureOrError,
    fieldStatusClear,
    formFieldsApiError,
    setStatus,
    values,
    valuesPrevious,
  ]);

  useEffect(() => {
    if (!failureOrError) return setFailureOrErrorCommon(undefined);
    if (
      failureOrErrorPrevious &&
      dataObjectsDeepEqual(failureOrError, failureOrErrorPrevious)
    ) {
      return;
    }
    if (failureOrError instanceof FailureApi) {
      const failuresApi = failureOrError.failures;
      if (failuresApi) {
        const [fieldsForm, fieldsCommon] = Object.keys(failuresApi).reduce(
          (fields, field) => {
            if (valuesFields.includes(field)) {
              fields[0].push(field);
            } else {
              fields[1].push(field);
            }
            return fields;
          },
          [[], []] as [string[], string[]],
        );
        const errorsForm = Object.fromEntries(
          fieldsForm.map((field) => [
            field,
            failuresApi[field].code === "API__ROOT__VALIDATION_ERROR"
              ? failuresApi[field].payload.body.message
              : failureTranslationGet(failuresApi[field]),
          ]),
        ) as unknown as FormikErrors<IFormValues>;
        setFormFieldsApiError(errorsForm);
        const errorCommon = fieldsCommon.length
          ? new FailureApi(
              "API__ROOT__VALIDATION_ERROR",
              failureOrError.payload,
              {
                ...Object.fromEntries(
                  fieldsCommon.map((field) => [field, failuresApi[field]]),
                ),
              },
            )
          : undefined;
        if (errorCommon) setFailureOrErrorCommon(errorCommon);
      } else {
        setFailureOrErrorCommon(failureOrError);
      }
    } else if (isGqlError(failureOrError)) {
      const formFieldErrors = failureOrError.response.errors?.reduce<
        FormikErrors<IFormValues>
      >((acc, error) => {
        const fieldname = error.fieldname;
        const message = error.message;

        if (fieldname && message && valuesFields.includes(fieldname)) {
          (acc as any)[fieldname] = message;
        }

        return acc;
      }, {});

      if (Object.keys(formFieldErrors).length) {
        setFormFieldsApiError(formFieldErrors);
      } else {
        setFailureOrErrorCommon(
          new Error(failureTranslationGet(failureOrError)),
        );
      }
    } else if (failureOrError instanceof Failure) {
      setFailureOrErrorCommon(failureOrError);
    } else if (failureOrError instanceof Error) {
      setFailureOrErrorCommon(failureOrError);
    }
  }, [
    failureOrError,
    failureOrErrorPrevious,
    failureTranslationGet,
    values,
    valuesFields,
    valuesPrevious,
  ]);

  useUpdateEffect(() => {
    if (isSubmitting) {
      setStatus(undefined);
    }
  }, [isSubmitting]);

  return useMemo(
    () =>
      failureOrErrorCommon ? (
        <div {...testAppendAttr("class", "form_errors")}>
          {failureOrErrorCommon && (
            <CardFailure clause={failureOrErrorCommon} />
          )}
        </div>
      ) : null,
    [failureOrErrorCommon],
  );
};
